import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import BackdropModal from 'components/portal/backdrop-modal'
import { withI18next } from 'lib/i18n/withI18next'
import compose from 'recompose/compose'
import Typo from 'constants/typography'
import Button from 'components/common/button'
import { BUTTON_TYPES } from 'constants/button'
import Radio from 'components/common/radio'
import { connect } from 'react-redux'
import FindThisItemAtStoreCta from 'components/pdp/interactions/find-this-item-at-store-cta'
import Badge from 'components/common/badge'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { BADGE_COLOR } from 'components/common/badge/const'
import styles from './add-to-bag-modal.scss'
import AddToBagCTA from '../add-to-bag-cta'
import { getCtaKey } from '../add-to-bag-cta/utils'
import SelectLabelItem from '../options-selector/label-item'

const AddToBagModal = ({
  t,
  product,
  isPhone,
  onSelected,
  showFallback,
  selectedOption,
  localWaitlists,
  setOptionMessage,
  setLocalWaitlists,
  setWaitlistSnackBarOpen,
  cashbackText,
  isShowEarnCashbackPDP,
  isFetched,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const openModal = useCallback(() => setIsModalOpen(true), [setIsModalOpen])
  const closeModal = useCallback(() => setIsModalOpen(false), [setIsModalOpen])
  const ctaKey = getCtaKey(localWaitlists, product, selectedOption)
  const handleBackdropClick = () => {
    closeModal()
    onSelected(null)
  }

  return (
    <React.Fragment>
      <style jsx>{styles}</style>
      <Button
        onClick={openModal}
        type={BUTTON_TYPES.primary}
        className="add-to-bag-modal__button"
      >
        {t(ctaKey)}
        {!!isShowEarnCashbackPDP && isFetched && (
          <span className={clsx(Typo.price2, 'pdp__cashback-text')}>
            {hasLocizeTranslation(
              t,
              cashbackText.key,
              cashbackText.text,
            ).replace('%d', product.cashback.cashback_earned_formatted)}
          </span>
        )}
      </Button>
      <BackdropModal
        zIndex={5}
        isOpen={isModalOpen}
        freezeBody={isPhone}
        onBackdropClick={handleBackdropClick}
      >
        <div className="add-to-bag-modal__wrapper">
          {product?.attributes?.map((sizeData) => {
            const isSelectedSize =
              sizeData?.id_product_attribute ===
              selectedOption?.id_product_attribute
            const isAddedProductMeasurement = sizeData?.is_new_measurement_added
            const isUpdatedProductMeasurement = sizeData?.is_measurement_updated

            return (
              <div
                key={sizeData?.id_product_attribute}
                className="add-to-bag-modal__content"
              >
                <button
                  type="button"
                  onClick={() => onSelected(sizeData)}
                  className={clsx('add-to-bag-modal__attr')}
                  data-cy={`add-to-bag-modal__attr-${sizeData?.size}`}
                >
                  <Radio
                    checked={isSelectedSize}
                    className={clsx('add-to-bag-modal__circle', {
                      'add-to-bag-modal__attr__out-of-stock':
                        sizeData?.stock?.is_sold_out &&
                        !product?.is_coming_soon,
                    })}
                  />
                  <div className="add-to-bag-modal__attr__text">
                    <span
                      className={clsx(
                        Typo.body2,
                        'add-to-bag-modal__label-body',
                        {
                          'add-to-bag-modal__attr__out-of-stock':
                            sizeData?.stock?.is_sold_out &&
                            !product?.is_coming_soon,
                        },
                      )}
                    >
                      {sizeData?.size}
                    </span>
                    <SelectLabelItem
                      item={sizeData}
                      isPhone={isPhone}
                      localWaitlists={localWaitlists}
                      isComingSoon={product?.is_coming_soon}
                    />
                    {isAddedProductMeasurement && (
                      <span style={{ marginLeft: '5px' }}>
                        <Badge
                          color={BADGE_COLOR.secondary}
                          contentClassName={clsx(Typo.caption)}
                          content={hasLocizeTranslation(t, 'BADGE_NEW', 'NEW')}
                        />
                      </span>
                    )}
                    {isUpdatedProductMeasurement && (
                      <span style={{ marginLeft: '5px' }}>
                        <Badge
                          color={BADGE_COLOR.secondary}
                          contentClassName={clsx(Typo.caption)}
                          content={hasLocizeTranslation(
                            t,
                            'PDP_SIZE_UPDATED',
                            'UPDATED',
                          )}
                        />
                      </span>
                    )}
                  </div>
                </button>
              </div>
            )
          })}
          <div className="add-to-bag-modal__sticky-content">
            <div
              className={clsx('add-to-bag-modal__find-item', {
                'add-to-bag-modal__find-item__disabled': !selectedOption,
              })}
            >
              <FindThisItemAtStoreCta
                product={product}
                selectedOption={selectedOption}
                setOptionMessage={setOptionMessage}
              />
            </div>
            <AddToBagCTA
              t={t}
              product={product}
              showFallback={showFallback}
              isDisabled={!selectedOption}
              localWaitlists={localWaitlists}
              selectedOption={selectedOption}
              setOptionMessage={setOptionMessage}
              setLocalWaitlists={setLocalWaitlists}
              setWaitlistSnackBarOpen={setWaitlistSnackBarOpen}
            />
          </div>
        </div>
      </BackdropModal>
    </React.Fragment>
  )
}

AddToBagModal.defaultProps = {
  selectedOption: null,
  setWaitlistSnackBarOpen: null,
  cashbackText: null,
  isShowEarnCashbackPDP: false,
  isFetched: false,
}

AddToBagModal.propTypes = {
  t: PropTypes.func.isRequired,
  isPhone: PropTypes.bool.isRequired,
  onSelected: PropTypes.func.isRequired,
  selectedOption: PropTypes.shape({
    id_product_attribute: PropTypes.number,
    is_new_measurement_added: PropTypes.bool,
  }),
  localWaitlists: PropTypes.shape({}).isRequired,
  product: PropTypes.shape({
    attributes: PropTypes.arrayOf(PropTypes.shape({})),
    id_product: PropTypes.number.isRequired,
    is_new_size_method: PropTypes.bool,
    is_coming_soon: PropTypes.bool,
    stock: PropTypes.shape({
      quantity: PropTypes.number,
      is_sold_out: PropTypes.bool,
    }),
    cashback: PropTypes.shape({
      cashback_earned: PropTypes.number,
      cashback_earned_formatted: PropTypes.string,
    }),
  }).isRequired,
  setLocalWaitlists: PropTypes.func.isRequired,
  setOptionMessage: PropTypes.func.isRequired,
  showFallback: PropTypes.func.isRequired,
  setWaitlistSnackBarOpen: PropTypes.func,
  cashbackText: PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
  }),
  isShowEarnCashbackPDP: PropTypes.bool,
  isFetched: PropTypes.bool,
}

export default compose(
  connect((state) => ({
    isPhone: state.device.isPhone,
  })),
  withI18next(),
)(AddToBagModal)
